import React from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Divider, 
  Grid, 
  Paper, 
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stepper,
  Step,
  StepLabel
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { 
  ArrowLeft, 
  //Check, 
  BrainCircuit, 
  Users, 
  Database, 
  BarChart4, 
  LineChart,
  Lightbulb,
  Fingerprint,
  Lock,
  Layers,
  Timer,
  Award,
  DollarSign,
  Mail
} from 'lucide-react';

interface SavvyDataSolutionDetailProps {
  onBack: () => void;
  onAddToEmail?: () => void;
}

const SectionContainer = styled(Paper)(({ theme }) => ({
  background: 'rgba(26, 26, 46, 0.6)',
  backdropFilter: 'blur(10px)',
  borderRadius: theme.spacing(2),
  border: '1px solid rgba(138, 43, 226, 0.2)',
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
}));

const FeatureItem = styled(ListItem)(({ theme }) => ({
  padding: theme.spacing(1, 0),
  '&:hover': {
    background: 'rgba(138, 43, 226, 0.1)',
  },
}));

const BackButton = styled(Button)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  color: '#00FFFF',
  borderColor: 'rgba(0, 255, 255, 0.5)',
  '&:hover': {
    background: 'linear-gradient(90deg, #003366, #00CC99, #8A2BE2, #FF6F00)',
    backgroundSize: '300% 100%',
    animation: 'gradient 6s linear infinite',
    transform: 'translateY(-2px)',
    boxShadow: '0 5px 15px rgba(138, 43, 226, 0.2)',
    border: '1px solid rgba(138, 43, 226, 0.3)',
  },
}));

const SavvyDataSolutionDetail: React.FC<SavvyDataSolutionDetailProps> = ({ onBack, onAddToEmail }) => {
  return (
    <Box>
      <BackButton
        variant="outlined"
        startIcon={<ArrowLeft size={16} />}
        onClick={onBack}
      >
        Back to Proposals
      </BackButton>
      
      <Typography variant="h4" sx={{ color: '#fff', mb: 3 }}>
        SavvyData Marketing Platform
      </Typography>
      
      <SectionContainer>
        <Typography variant="h6" sx={{ color: '#00FFFF', mb: 2 }}>
          Solution Overview
        </Typography>
        <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.8)', mb: 2 }}>
          SavvyData is an intelligent B2B sales and marketing platform that leverages AI to identify, engage, 
          and convert high-value prospects. Our platform combines advanced prospect research, personalized 
          communication, and data-driven insights to optimize your sales process.
        </Typography>
        
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
          <Chip 
            icon={<BrainCircuit size={16} />} 
            label="AI-Powered" 
            sx={{ 
              backgroundColor: 'rgba(138, 43, 226, 0.2)',
              color: '#fff' 
            }} 
          />
          <Chip 
            icon={<Database size={16} />} 
            label="Data-Driven" 
            sx={{ 
              backgroundColor: 'rgba(0, 204, 153, 0.2)',
              color: '#fff' 
            }} 
          />
          <Chip 
            icon={<Users size={16} />} 
            label="B2B Focused" 
            sx={{ 
              backgroundColor: 'rgba(0, 255, 255, 0.2)',
              color: '#fff' 
            }} 
          />
          <Chip 
            icon={<BarChart4 size={16} />} 
            label="Results Oriented" 
            sx={{ 
              backgroundColor: 'rgba(255, 105, 0, 0.2)',
              color: '#fff' 
            }} 
          />
        </Box>
      </SectionContainer>
      
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <SectionContainer>
            <Typography variant="h6" sx={{ color: '#00FFFF', mb: 2 }}>
              Key Features
            </Typography>
            
            <List>
              <FeatureItem>
                <ListItemIcon>
                  <BrainCircuit size={24} color="#8A2BE2" />
                </ListItemIcon>
                <ListItemText 
                  primary="Intelligent Prospect Research" 
                  secondary="Multi-source data collection and automated qualification" 
                  primaryTypographyProps={{ color: '#fff' }}
                  secondaryTypographyProps={{ color: 'rgba(255, 255, 255, 0.7)' }}
                />
              </FeatureItem>
              
              <FeatureItem>
                <ListItemIcon>
                  <Fingerprint size={24} color="#00CC99" />
                </ListItemIcon>
                <ListItemText 
                  primary="Deep Persona Understanding" 
                  secondary="Comprehensive buyer persona analysis and behavior prediction" 
                  primaryTypographyProps={{ color: '#fff' }}
                  secondaryTypographyProps={{ color: 'rgba(255, 255, 255, 0.7)' }}
                />
              </FeatureItem>
              
              <FeatureItem>
                <ListItemIcon>
                  <LineChart size={24} color="#00FFFF" />
                </ListItemIcon>
                <ListItemText 
                  primary="Smart Engagement" 
                  secondary="Personalized email communication and multi-channel tracking" 
                  primaryTypographyProps={{ color: '#fff' }}
                  secondaryTypographyProps={{ color: 'rgba(255, 255, 255, 0.7)' }}
                />
              </FeatureItem>
              
              <FeatureItem>
                <ListItemIcon>
                  <Database size={24} color="#FF6F00" />
                </ListItemIcon>
                <ListItemText 
                  primary="CRM Integration" 
                  secondary="Seamless HubSpot and Salesforce integration" 
                  primaryTypographyProps={{ color: '#fff' }}
                  secondaryTypographyProps={{ color: 'rgba(255, 255, 255, 0.7)' }}
                />
              </FeatureItem>
              
              <FeatureItem>
                <ListItemIcon>
                  <Lightbulb size={24} color="#FFC107" />
                </ListItemIcon>
                <ListItemText 
                  primary="Next-Best-Action Recommendations" 
                  secondary="AI-driven guidance on when and how to engage prospects" 
                  primaryTypographyProps={{ color: '#fff' }}
                  secondaryTypographyProps={{ color: 'rgba(255, 255, 255, 0.7)' }}
                />
              </FeatureItem>
            </List>
          </SectionContainer>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <SectionContainer>
            <Typography variant="h6" sx={{ color: '#00FFFF', mb: 2 }}>
              Benefits
            </Typography>
            
            <List>
              <FeatureItem>
                <ListItemIcon>
                  <Timer size={24} color="#00CC99" />
                </ListItemIcon>
                <ListItemText 
                  primary="Save 15+ hours per week on research" 
                  secondary="Automated data collection and analysis frees up your sales team" 
                  primaryTypographyProps={{ color: '#fff' }}
                  secondaryTypographyProps={{ color: 'rgba(255, 255, 255, 0.7)' }}
                />
              </FeatureItem>
              
              <FeatureItem>
                <ListItemIcon>
                  <DollarSign size={24} color="#00FFFF" />
                </ListItemIcon>
                <ListItemText 
                  primary="Increase conversion rates by 35%" 
                  secondary="Personalized engagement based on prospect persona and behavior" 
                  primaryTypographyProps={{ color: '#fff' }}
                  secondaryTypographyProps={{ color: 'rgba(255, 255, 255, 0.7)' }}
                />
              </FeatureItem>
              
              <FeatureItem>
                <ListItemIcon>
                  <Award size={24} color="#FFC107" />
                </ListItemIcon>
                <ListItemText 
                  primary="Improve prospect qualification" 
                  secondary="Focus on high-value prospects with 85% greater accuracy" 
                  primaryTypographyProps={{ color: '#fff' }}
                  secondaryTypographyProps={{ color: 'rgba(255, 255, 255, 0.7)' }}
                />
              </FeatureItem>
              
              <FeatureItem>
                <ListItemIcon>
                  <Layers size={24} color="#8A2BE2" />
                </ListItemIcon>
                <ListItemText 
                  primary="Richer prospect insights" 
                  secondary="Deep understanding of decision-making patterns and preferences" 
                  primaryTypographyProps={{ color: '#fff' }}
                  secondaryTypographyProps={{ color: 'rgba(255, 255, 255, 0.7)' }}
                />
              </FeatureItem>
              
              <FeatureItem>
                <ListItemIcon>
                  <Lock size={24} color="#FF6F00" />
                </ListItemIcon>
                <ListItemText 
                  primary="Enterprise-grade security" 
                  secondary="SOC 2 Type II certified with GDPR compliance" 
                  primaryTypographyProps={{ color: '#fff' }}
                  secondaryTypographyProps={{ color: 'rgba(255, 255, 255, 0.7)' }}
                />
              </FeatureItem>
            </List>
          </SectionContainer>
        </Grid>
      </Grid>
      
      <SectionContainer>
        <Typography variant="h6" sx={{ color: '#00FFFF', mb: 3 }}>
          Implementation Timeline
        </Typography>
        
        <Stepper alternativeLabel>
          <Step active>
            <StepLabel StepIconProps={{ style: { color: '#8A2BE2' } }}>
              <Typography variant="body2" sx={{ color: '#fff' }}>
                Initial Setup
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                Week 1-2
              </Typography>
            </StepLabel>
          </Step>
          <Step active>
            <StepLabel StepIconProps={{ style: { color: '#8A2BE2' } }}>
              <Typography variant="body2" sx={{ color: '#fff' }}>
                CRM Integration
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                Week 3
              </Typography>
            </StepLabel>
          </Step>
          <Step active>
            <StepLabel StepIconProps={{ style: { color: '#8A2BE2' } }}>
              <Typography variant="body2" sx={{ color: '#fff' }}>
                Data Migration
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                Week 4
              </Typography>
            </StepLabel>
          </Step>
          <Step active>
            <StepLabel StepIconProps={{ style: { color: '#8A2BE2' } }}>
              <Typography variant="body2" sx={{ color: '#fff' }}>
                Team Training
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                Week 5
              </Typography>
            </StepLabel>
          </Step>
          <Step>
            <StepLabel StepIconProps={{ style: { color: '#8A2BE2' } }}>
              <Typography variant="body2" sx={{ color: '#fff' }}>
                Go Live
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                Week 6
              </Typography>
            </StepLabel>
          </Step>
        </Stepper>
      </SectionContainer>
      
      <SectionContainer>
        <Typography variant="h6" sx={{ color: '#00FFFF', mb: 2 }}>
          Pricing & ROI
        </Typography>
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
          <Box>
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 0.5 }}>
              Implementation Cost
            </Typography>
            <Typography variant="h4" sx={{ color: '#fff' }}>
              $25,000
            </Typography>
          </Box>
          
          <Box>
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 0.5 }}>
              Annual Subscription
            </Typography>
            <Typography variant="h4" sx={{ color: '#fff' }}>
              $50,000
            </Typography>
          </Box>
          
          <Box>
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 0.5 }}>
              Estimated ROI
            </Typography>
            <Typography variant="h4" sx={{ color: '#00CC99' }}>
              324%
            </Typography>
          </Box>
        </Box>
        
        <Divider sx={{ my: 2, borderColor: 'rgba(138, 43, 226, 0.2)' }} />
        
        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
          Average customers see a return on investment within 4-6 months based on
          increased conversion rates, time savings, and improved prospect qualification.
        </Typography>
      </SectionContainer>
      
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, gap: 2 }}>
        <Button 
          variant="contained"
          size="large"
          startIcon={<Mail />}
          onClick={onAddToEmail}
          sx={{
            background: 'linear-gradient(45deg, #00CC99 30%, #8A2BE2 90%)',
            boxShadow: '0 3px 5px 2px rgba(0, 204, 153, .3)',
            color: 'white',
            padding: '12px 32px',
          }}
        >
          Add Proposal to Email
        </Button>
        
        <Button 
          variant="contained"
          size="large"
          sx={{
            background: 'linear-gradient(45deg, #8A2BE2 30%, #00CC99 90%)',
            boxShadow: '0 3px 5px 2px rgba(138, 43, 226, .3)',
            color: 'white',
            padding: '12px 32px',
          }}
        >
          Schedule Demo
        </Button>
      </Box>
    </Box>
  );
};

export default SavvyDataSolutionDetail; 